import { useCallback, useEffect, useState, useMemo } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Link2, Unlink, Trash2, CheckCircle2, AlertTriangle, Landmark, FileText, X, Zap } from "lucide-react";
import { MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { useAuth } from "../../context/AuthContext";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `GET /finance/rekonsiliasi` mengurut `tanggal asc` lalu memotong di
// backend TANPA parameter urut dari klien (lihat rekonsiliasi.py) — dibatasi
// per akun+periode (satu bulan) jadi batasnya nyaris mustahil kesentuh,
// tapi pola query-nya sama dengan daftar lain yang bisa terpotong, jadi
// tetap didampingi `BatasDaftarNotice` demi konsisten & jujur.
const REKON_STATEMENT_BATAS = 5000;
const REKON_UNMATCHED_BATAS = 500;

// Label kategori akun untuk pengelompokan dropdown. Sengaja peta kecil di
// berkas ini, bukan impor dari FinanceCOA: yang dibutuhkan cuma tiga kategori
// kas, dan menariknya dari registry akan menambah satu permintaan jaringan
// hanya untuk tiga kata.
const KATEGORI_LABEL = { bank: "Bank", kas: "Kas", petty_cash: "Kas Kecil" };


export default function FinanceRekonsiliasi() {
  const { can } = useAuth();
  const [banks, setBanks] = useState([]);
  const [akunId, setAkunId] = useState("");
  const [periode, setPeriode] = useState(bulanIniISO());
  // Rentang tanggal bebas (brief owner 29 Agu). Kalau diisi, ia MENANG atas
  // pilihan bulan — dua penyaring tanggal yang aktif bersamaan saling memotong
  // dan menghasilkan daftar kosong yang terlihat seperti "tidak ada mutasi".
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [rentangAktif, setRentangAktif] = useState({ dari: "", sampai: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/finance/accounts/kas-bank").then(r => {
      // SEMUA akun kas/bank/petty, tidak lagi hanya bank & kas — brief owner
      // meminta "kategori per akun", dan kas kecil juga punya mutasi yang perlu
      // dicocokkan. Endpointnya sudah membatasi ke akun yang boleh dipakai
      // transaksi kas, jadi tidak ada akun lain yang ikut nyasar ke sini.
      const bs = r.data || [];
      setBanks(bs);
      if (bs.length > 0) setAkunId(current => current || bs[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!akunId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const p = new URLSearchParams({ akun_id: akunId });
      if (rentangAktif.dari || rentangAktif.sampai) {
        if (rentangAktif.dari) p.set("dari", rentangAktif.dari);
        if (rentangAktif.sampai) p.set("sampai", rentangAktif.sampai);
      } else {
        p.set("periode", periode);
      }
      const r = await api.get(`/finance/rekonsiliasi?${p.toString()}`);
      setData(r.data);
    } catch (e) {
      setLoadError(e);
      const pesan = e?.response?.data?.detail;
      toast.error(e?.response?.status === 400 && pesan ? pesan : "Gagal load");
    }
    finally { setLoading(false); }
  }, [akunId, periode, rentangAktif]);
  useEffect(() => { load(); }, [load]);

  const autoMatch = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/rekonsiliasi/auto-match?akun_id=${akunId}`);
      // Backend membedakan KENAPA hasilnya nol (belum ada mutasi, semua sudah
      // match, tidak ada kandidat, atau ada kandidat tapi ambigu) — jangan
      // sembunyikan itu di balik satu toast generik "0 baris ter-match".
      if (r.data.matched > 0) toast.success(r.data.message);
      else if (r.data.reason === "all_matched") toast.info(r.data.message);
      else toast.warning(r.data.message);
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  const unmatch = async (sid) => {
    if (!confirm("Batalkan match?")) return;
    try { await api.post(`/finance/rekonsiliasi/${sid}/unmatch`); toast.success("Match dibatalkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (sid) => {
    if (!confirm("Hapus baris statement ini?")) return;
    try { await api.delete(`/finance/rekonsiliasi/${sid}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const manualMatch = async (sid, txnId) => {
    try { await api.post(`/finance/rekonsiliasi/${sid}/match`, { transaction_id: txnId }); toast.success("Match tersimpan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Urutan bawaan (tanggal asc dari backend) tetap sampai header diklik.
  const stmtSort = useSortState(null);
  const sortedStatements = useMemo(() => sortRows(data?.statements || [], stmtSort.sortKey, stmtSort.sortDir, {
    tanggal: (s) => s.tanggal || "",
    deskripsi: (s) => s.deskripsi || "",
    masuk: (s) => (s.jenis === "masuk" ? s.nominal : 0),
    keluar: (s) => (s.jenis === "keluar" ? s.nominal : 0),
  }), [data, stmtSort.sortKey, stmtSort.sortDir]);

  const txnSort = useSortState(null);
  const sortedUnmatchedTxns = useMemo(() => sortRows(data?.unmatched_txns || [], txnSort.sortKey, txnSort.sortDir, {
    tanggal: (t) => t.tanggal || "",
    kategori: (t) => t.kategori || "",
    deskripsi: (t) => t.deskripsi || "",
    nominal: (t) => t.nominal || 0,
  }), [data, txnSort.sortKey, txnSort.sortDir]);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-rekonsiliasi">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Bank Reconciliation</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Rekonsiliasi Bank</h1>
          <p className="text-sm text-slate-600 mt-1">Upload mutasi bank · auto-match dengan transaksi sistem · lihat selisih</p>
        </div>
        <div className="flex gap-2">
          {/* Akun dikelompokkan per KATEGORI (brief owner 29 Agu) — bank, kas,
              dan kas kecil punya perlakuan berbeda saat dicocokkan, dan
              daftarnya jadi panjang kalau semuanya dicampur tanpa label. */}
          <select value={akunId} onChange={e => setAkunId(e.target.value)} data-testid="rekon-akun"
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm">
            {["bank", "kas", "petty_cash"].map(kat => {
              const isi = banks.filter(b => b.category === kat);
              if (isi.length === 0) return null;
              return (
                <optgroup key={kat} label={KATEGORI_LABEL[kat] || kat}>
                  {isi.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </optgroup>
              );
            })}
            {/* Akun berkategori lain (kalau registry menambah kategori kas baru)
                tetap muncul — kalau disaring keluar, akun yang sah jadi tidak
                bisa direkonsiliasi tanpa satu pun pesan. */}
            {banks.some(b => !["bank", "kas", "petty_cash"].includes(b.category)) && (
              <optgroup label="Lainnya">
                {banks.filter(b => !["bank", "kas", "petty_cash"].includes(b.category))
                  .map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </optgroup>
            )}
          </select>
          {!(rentangAktif.dari || rentangAktif.sampai) && (
            <MonthPicker value={periode} onChange={setPeriode} data-testid="rekon-periode" className="w-36" />
          )}
          {/* Gerbang izin di frontend HARUS pakai kunci modul yang sama dengan
              require_permission di backend (finance_rekonsiliasi) — auditor
              (view-only) sebelumnya melihat tombol ini lalu ditolak 403 saat
              diklik (aturan #3 CLAUDE.md: jangan tampilkan tombol yang berujung 403). */}
          {can("finance_rekonsiliasi", "update") && (
            <button onClick={autoMatch} disabled={busy || !data} data-testid="rekon-automatch" className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"><Zap className="w-4 h-4" /> Auto-Match</button>
          )}
          {can("finance_rekonsiliasi", "create") && (
            <button onClick={() => setShowUpload(true)} disabled={!akunId} data-testid="rekon-upload-btn" className="h-10 px-4 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"><Upload className="w-4 h-4" /> Upload CSV</button>
          )}
        </div>
      </div>


      {/* Rentang tanggal bebas (brief owner 29 Agu). */}
      <div className="mb-5 bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-end gap-3"
           data-testid="rekon-filter-periode">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari Tanggal</label>
          <input type="date" value={dari} onChange={e => setDari(e.target.value)}
            data-testid="rekon-dari" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai Tanggal</label>
          <input type="date" value={sampai} onChange={e => setSampai(e.target.value)}
            data-testid="rekon-sampai" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <button data-testid="rekon-tampilkan"
          onClick={() => {
            if (dari && sampai && dari > sampai) { toast.error("Tanggal 'dari' melewati tanggal 'sampai'"); return; }
            setRentangAktif({ dari, sampai });
          }}
          className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          Tampilkan
        </button>
        {(rentangAktif.dari || rentangAktif.sampai) && (
          <>
            <button onClick={() => { setDari(""); setSampai(""); setRentangAktif({ dari: "", sampai: "" }); }}
              data-testid="rekon-reset"
              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
              Reset ke per bulan
            </button>
            <p className="text-[11px] text-slate-500">
              Rentang bebas aktif — pilihan bulan sengaja disembunyikan supaya tidak ada dua
              penyaring tanggal yang saling memotong.
            </p>
          </>
        )}
      </div>

      <LoadingState loading={loading && !!akunId} error={loadError} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-rekonsiliasi">
      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Saldo Sistem</p>
              <p className="font-display text-xl font-bold text-slate-900 mt-1">{idrShort(data.akun.saldo_sistem)}</p>
              <p className="text-[10px] text-slate-500 mt-1">{data.akun.name}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Saldo Bank (Statement)</p>
              <p className="font-display text-xl font-bold text-sky-700 mt-1">{idrShort(data.summary.saldo_bank)}</p>
              <p className="text-[10px] text-slate-500 mt-1">{data.summary.total_stmt} baris</p>
            </div>
            <div className={`border rounded-xl p-4 shadow-sm ${Math.abs(data.summary.selisih_vs_sistem) < 1 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${Math.abs(data.summary.selisih_vs_sistem) < 1 ? "text-emerald-700" : "text-amber-700"}`}>Selisih Bank vs Sistem</p>
              <p className={`font-display text-xl font-bold mt-1 ${Math.abs(data.summary.selisih_vs_sistem) < 1 ? "text-emerald-800" : "text-amber-800"}`}>{idrShort(data.summary.selisih_vs_sistem)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Status Match</p>
              <p className="font-display text-lg font-bold text-slate-900 mt-1"><span className="text-emerald-700">{data.summary.matched}</span> / <span className="text-slate-500">{data.summary.total_stmt}</span></p>
              <p className="text-[10px] text-rose-600 mt-1">{data.summary.unmatched} unmatched</p>
            </div>
          </div>

          {/* Statement table */}
          <BatasDaftarNotice shown={data.statements.length} batas={REKON_STATEMENT_BATAS} />
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><Landmark className="w-4 h-4 text-sky-600" /><p className="font-display font-bold text-base text-slate-900">Mutasi Bank</p></div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0 text-[11px] uppercase text-slate-500"><tr>
                  <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={stmtSort.sortKey} sortDir={stmtSort.sortDir} onSort={stmtSort.toggle} testid="rekon-sort-tanggal" />
                  <SortTh label="Deskripsi" sortKeyName="deskripsi" sortKey={stmtSort.sortKey} sortDir={stmtSort.sortDir} onSort={stmtSort.toggle} testid="rekon-sort-deskripsi" />
                  <SortTh label="Masuk" sortKeyName="masuk" sortKey={stmtSort.sortKey} sortDir={stmtSort.sortDir} onSort={stmtSort.toggle} align="right" testid="rekon-sort-masuk" />
                  <SortTh label="Keluar" sortKeyName="keluar" sortKey={stmtSort.sortKey} sortDir={stmtSort.sortDir} onSort={stmtSort.toggle} align="right" testid="rekon-sort-keluar" />
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-right">Aksi</th>
                </tr></thead>
                <tbody>
                  {data.statements.length === 0 ? <tr><td colSpan={6} className="p-10 text-center text-slate-400">Belum ada statement · Upload dulu</td></tr> :
                    sortedStatements.map(s => (
                      <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-3 py-2 font-mono text-xs">{formatTanggal(s.tanggal)}</td>
                        <td className="px-3 py-2">
                          <p className="text-slate-800">{s.deskripsi || "-"}</p>
                          {s.referensi && <p className="text-[10px] text-slate-400 font-mono">Ref: {s.referensi}</p>}
                          {/* auto_match_note diisi backend saat auto-match menemukan LEBIH DARI SATU
                              kandidat sama-sama layak — sengaja TIDAK dipilihkan otomatis (lihat rekonsiliasi.py),
                              jadi pengguna perlu tahu kenapa baris ini masih unmatched. */}
                          {s.auto_match_note && <p className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" /> {s.auto_match_note}</p>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{s.jenis === "masuk" ? idr(s.nominal) : "-"}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-700">{s.jenis === "keluar" ? idr(s.nominal) : "-"}</td>
                        <td className="px-3 py-2 text-center">
                          {s.status === "matched" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3" /> {s.match_type || "matched"}</span>
                          ) : s.auto_match_note ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-50 text-amber-700 border-amber-200"><AlertTriangle className="w-3 h-3" /> ambigu</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"><AlertTriangle className="w-3 h-3" /> unmatched</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {s.status === "matched" ? (
                            can("finance_rekonsiliasi", "update") && <button onClick={() => unmatch(s.id)} title="Batalkan match" className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1"><Unlink className="w-3 h-3" /> Batal</button>
                          ) : (
                            can("finance_rekonsiliasi", "update") && <ManualMatchPicker stmt={s} txns={data.unmatched_txns} akunId={akunId} onMatch={(txnId) => manualMatch(s.id, txnId)} />
                          )}
                          {can("finance_rekonsiliasi", "delete") && <button onClick={() => del(s.id)} className="ml-2 text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {/* Unmatched system txns */}
          {data.unmatched_txns.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-600" /><p className="font-display font-bold text-base text-slate-900">Transaksi Sistem Belum Ter-rekonsiliasi ({data.unmatched_txns.length})</p></div>
              <BatasDaftarNotice shown={data.unmatched_txns.length} batas={REKON_UNMATCHED_BATAS} />
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b sticky top-0 text-[11px] uppercase text-slate-500"><tr>
                    <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={txnSort.sortKey} sortDir={txnSort.sortDir} onSort={txnSort.toggle} testid="rekon-unmatched-sort-tanggal" />
                    <SortTh label="Kategori" sortKeyName="kategori" sortKey={txnSort.sortKey} sortDir={txnSort.sortDir} onSort={txnSort.toggle} testid="rekon-unmatched-sort-kategori" />
                    <SortTh label="Deskripsi" sortKeyName="deskripsi" sortKey={txnSort.sortKey} sortDir={txnSort.sortDir} onSort={txnSort.toggle} testid="rekon-unmatched-sort-deskripsi" />
                    <th className="px-3 py-2 text-center">Jenis</th>
                    <SortTh label="Nominal" sortKeyName="nominal" sortKey={txnSort.sortKey} sortDir={txnSort.sortDir} onSort={txnSort.toggle} align="right" testid="rekon-unmatched-sort-nominal" />
                  </tr></thead>
                  <tbody>
                    {sortedUnmatchedTxns.map(t => {
                      // Transfer menyentuh dua akun — arahnya (masuk/keluar) tergantung akun
                      // rekonsiliasi yang SEDANG dilihat, bukan properti tetap milik transaksinya.
                      const isTransferOut = t.jenis === "transfer" && t.sumber_akun_id === akunId;
                      const jenisLabel = t.jenis === "transfer" ? (isTransferOut ? "transfer (keluar)" : "transfer (masuk)") : t.jenis;
                      const isMasuk = t.jenis === "uang_masuk" || (t.jenis === "transfer" && !isTransferOut);
                      return (
                        <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-3 py-2 font-mono text-xs">{formatTanggal(t.tanggal)}</td>
                          <td className="px-3 py-2 text-slate-700 text-xs">{t.kategori}</td>
                          <td className="px-3 py-2 text-slate-700 text-xs truncate max-w-xs">{t.deskripsi}</td>
                          <td className="px-3 py-2 text-center"><span className={`text-[10px] font-semibold ${isMasuk ? "text-emerald-700" : "text-rose-700"}`}>{jenisLabel}</span></td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{idr(t.nominal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      </LoadingState>

      {showUpload && <UploadModal akunId={akunId} periode={periode} onClose={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}

function ManualMatchPicker({ stmt, txns, akunId, onMatch }) {
  // Toleransi <1 rupiah — SAMA dengan backend (lihat NOMINAL_EPS di rekonsiliasi.py):
  // cuma menampung pembulatan floating-point, bukan pelonggaran nominal beda.
  const candidates = txns.filter(t => {
    // Potongan Bayar Sekaligus (Pelunasan Pembelian) tidak pernah muncul
    // sendirian di rekening koran — hanya ditawarkan sebagai GRUP di bawah.
    if (t.bayar_banyak_id && t.jenis === "uang_keluar") return false;
    if (Math.abs(t.nominal - stmt.nominal) >= 1) return false;
    if (t.jenis === "transfer") {
      // Transfer punya dua sisi — sisi yang relevan untuk baris mutasi ini
      // ditentukan oleh arah (masuk/keluar) DAN akun rekonsiliasi yang aktif.
      return stmt.jenis === "keluar" ? t.sumber_akun_id === akunId : t.tujuan_akun_id === akunId;
    }
    const expectJenis = stmt.jenis === "masuk" ? "uang_masuk" : "uang_keluar";
    return t.jenis === expectJenis;
  });
  // SATU transfer Bayar Sekaligus = beberapa transaksi ber-`bayar_banyak_id`
  // sama. Jumlahnya yang dibandingkan dengan baris mutasi; memilih grup
  // mengirim id anggota pertamanya dan backend mencocokkan SELURUH grup
  // (`rekonsiliasi.py::manual_match`).
  const grup = {};
  if (stmt.jenis === "keluar") {
    txns.filter(t => t.bayar_banyak_id && t.jenis === "uang_keluar" && t.sumber_akun_id === akunId)
      .forEach(t => {
        const g = grup[t.bayar_banyak_id] || (grup[t.bayar_banyak_id] = { ids: [], total: 0, tanggal: t.tanggal });
        g.ids.push(t.id);
        g.total += t.nominal || 0;
      });
  }
  const grupCocok = Object.values(grup).filter(g => Math.abs(g.total - stmt.nominal) < 1);
  if (candidates.length === 0 && grupCocok.length === 0) return <span className="text-[10px] text-slate-400">no match</span>;
  return (
    <select onChange={e => e.target.value && onMatch(e.target.value)} defaultValue="" className="h-7 px-2 border border-slate-200 rounded text-xs bg-white"
            data-testid={`rekon-match-${stmt.id}`}>
      <option value="">Match ke...</option>
      {grupCocok.map(g => (
        <option key={g.ids[0]} value={g.ids[0]}>{g.tanggal} · Bayar sekaligus {g.ids.length} tagihan</option>
      ))}
      {candidates.slice(0, 10).map(t => <option key={t.id} value={t.id}>{t.tanggal} · {t.kategori}</option>)}
    </select>
  );
}

// Diekspor supaya layar Buku Besar memakai KOMPONEN YANG SAMA (brief owner
// 29 Agu meminta fitur unggah CSV yang "seperti pada menu Rekonsiliasi
// Bank"). Menyalinnya ke sana berarti dua parser CSV yang bisa berpisah
// jalan — kolom yang dikenali di satu layar tidak dikenali di layar lain.
export function UploadModal({ akunId, periode, onClose }) {
  const [rows, setRows] = useState([]);
  const [rawText, setRawText] = useState("");
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState("AUTO");

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const header = lines[0].toLowerCase().split(",").map(x => x.trim());
    const idxTgl = header.findIndex(h => /tanggal|date/.test(h));
    const idxDesc = header.findIndex(h => /desk|desc|keterangan|narrative|remark/.test(h));
    const idxNom = header.findIndex(h => /nominal|amount|jumlah|rupiah/.test(h));
    const idxJns = header.findIndex(h => /jenis|type|dr|cr|debit|kredit/.test(h));
    const idxRef = header.findIndex(h => /ref|reference/.test(h));
    if (idxTgl < 0 || idxNom < 0) { toast.error("CSV harus punya kolom tanggal & nominal"); return []; }
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(",").map(x => x.trim());
      if (cells.length < 2) continue;
      const nominal = Number(cells[idxNom].replace(/[^\d.-]/g, ""));
      if (!nominal) continue;
      let jenis = "masuk";
      if (idxJns >= 0) {
        const v = cells[idxJns].toLowerCase();
        if (/keluar|debit|dr|out/.test(v)) jenis = "keluar";
        else if (/masuk|kredit|cr|in/.test(v)) jenis = "masuk";
      } else if (nominal < 0) { jenis = "keluar"; }
      out.push({
        tanggal: cells[idxTgl],
        deskripsi: idxDesc >= 0 ? cells[idxDesc] : "",
        nominal: Math.abs(nominal), jenis,
        referensi: idxRef >= 0 ? cells[idxRef] : null,
      });
    }
    return out;
  };

    const handleFile = (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      // XLSX or PDF uploaded → send to server for native parse
      if (/\.xlsx$/i.test(f.name) || /\.pdf$/i.test(f.name)) {
        (async () => {
          setBusy(true);
          try {
            const fd = new FormData();
            fd.append("akun_id", akunId);
            fd.append("periode", periode || "");
            fd.append("bank", bank);
            fd.append("file", f);
            const endpoint = /\.pdf$/i.test(f.name) ? "/finance/rekonsiliasi/upload-pdf" : "/finance/rekonsiliasi/upload-xlsx";
            const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(r.data.message);
            onClose();
          } catch (e2) { toast.error(e2?.response?.data?.detail || "Gagal upload"); }
          finally { setBusy(false); }
        })();
        return;
      }
      // CSV client-side
      const rd = new FileReader();
      rd.onload = (ev) => { const text = ev.target.result; setRawText(text); setRows(parseCSV(text)); };
      rd.readAsText(f);
    };

  const handleTextChange = (t) => { setRawText(t); setRows(parseCSV(t)); };

  const submit = async () => {
    if (rows.length === 0) return toast.error("Tidak ada data valid");
    setBusy(true);
    try { const r = await api.post("/finance/rekonsiliasi/upload", { akun_id: akunId, periode, statements: rows }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
    finally { setBusy(false); }
  };

  // `dangerous` — CSV yang sudah dipilih/di-paste (bisa ratusan baris) hilang
  // kalau modal tertutup tak sengaja sebelum di-upload.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || rows.length === 0, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="rekon-upload-modal" className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-lg">Upload Mutasi Bank</h3>
            <p className="text-xs text-slate-500 mt-0.5">Upload file CSV atau paste kolom: <b>tanggal,deskripsi,nominal,jenis,referensi</b></p>
          </div>
          <button onClick={guard.requestClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Template Bank (XLSX)</label>
          <select value={bank} onChange={e => setBank(e.target.value)} data-testid="rekon-bank-preset" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm">
            <option value="AUTO">Auto-detect (untuk file tak dikenal)</option>
            <option value="BCA">BCA</option>
            <option value="Mandiri">Mandiri</option>
            <option value="BRI">BRI</option>
            <option value="BNI">BNI</option>
          </select>
        </div>
        <input type="file" accept=".csv,.xlsx,.pdf,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} data-testid="rekon-file-input" className="text-sm mb-2" />
        <p className="text-[11px] text-slate-500 mb-2">📁 Support: <b>.csv</b> (browser), <b>.xlsx</b> atau <b>.pdf</b> (server auto-parse). PDF: pilih bank preset dulu untuk hasil lebih akurat.</p>
        <textarea value={rawText} onChange={e => handleTextChange(e.target.value)} rows={6} placeholder="tanggal,deskripsi,nominal,jenis&#10;2026-07-01,Transfer Budi,5000000,masuk&#10;2026-07-02,Bayar listrik,850000,keluar" className="w-full border border-slate-200 rounded-lg text-xs font-mono p-3 outline-none focus:border-sky-500 mb-3" data-testid="rekon-paste" />
        {rows.length > 0 && (
          <>
            <p className="text-xs font-semibold text-emerald-700 mb-2">✓ Parsed {rows.length} baris valid</p>
            <div className="border border-slate-200 rounded-lg overflow-y-auto flex-1 max-h-64 mb-3">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  <th className="px-2 py-1 text-left">Tanggal</th><th className="px-2 py-1 text-left">Deskripsi</th>
                  <th className="px-2 py-1 text-right">Nominal</th><th className="px-2 py-1 text-center">Jenis</th>
                </tr></thead>
                <tbody>
                  {rows.slice(0, 100).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{r.tanggal}</td>
                      <td className="px-2 py-1 truncate max-w-xs">{r.deskripsi}</td>
                      <td className="px-2 py-1 text-right font-mono">{idr(r.nominal)}</td>
                      <td className={`px-2 py-1 text-center font-semibold ${r.jenis === "masuk" ? "text-emerald-700" : "text-rose-700"}`}>{r.jenis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {guard.confirming && (
          <div className="-mx-6 mb-3"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="rekon-upload-guard" /></div>
        )}
        <div className="flex gap-2"><button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy || rows.length === 0} data-testid="rekon-upload-submit" className="flex-1 h-10 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-medium disabled:opacity-50">{busy ? "Uploading..." : `Upload ${rows.length} baris`}</button></div>
      </div>
    </div>
  );
}
